/**
 * Class reminders configuration for one training series.
 *
 * A facilitator picks when reminders go out (offsets before each session),
 * the channel (email / text / both) and the message bodies; delivery runs
 * server-side every 5 minutes through the platform's existing staff→member
 * email (SendGrid) and SMS (Twilio) paths, honoring member opt-outs and
 * logging to the member's communication history.
 * API: GET/PUT /api/workforce/training-series/{series_id}/reminders (+ /sends).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Bell, Loader2, Save } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';

interface ReminderConfig {
  enabled: boolean;
  offsets_minutes: number[];
  channels: 'email' | 'sms' | 'both';
  timezone: string;
  email_subject: string | null;
  email_body: string | null;
  sms_body: string | null;
  email_template_id: number | null;
  sms_template_id: number | null;
}

interface TemplateOption {
  id: number;
  name: string;
  subject: string | null;
  body: string;
}

interface SendRow {
  id: number;
  session_title: string;
  start_time: string;
  channel: string;
  status: string;
  recipient: string | null;
  sent_at: string;
}

const OFFSET_CHOICES = [
  { minutes: 10080, label: '1 week before' },
  { minutes: 2880, label: '2 days before' },
  { minutes: 1440, label: 'Day before' },
  { minutes: 240, label: '4 hours before' },
  { minutes: 120, label: '2 hours before' },
  { minutes: 60, label: '1 hour before' },
];

export default function SeriesRemindersPanel({ seriesId }: { seriesId: string }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [cfg, setCfg] = useState<ReminderConfig>({
    enabled: true,
    offsets_minutes: [1440, 120],
    channels: 'both',
    timezone: 'America/Los_Angeles',
    email_subject: null,
    email_body: null,
    sms_body: null,
    email_template_id: null,
    sms_template_id: null,
  });
  const [defaults, setDefaults] = useState<{ email_subject: string; email_body: string; sms_body: string } | null>(null);
  const [templates, setTemplates] = useState<{ email: TemplateOption[]; sms: TemplateOption[] }>({ email: [], sms: [] });
  const [sends, setSends] = useState<SendRow[]>([]);
  const [showSends, setShowSends] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(getWeaveinApiUrl(`/api/workforce/training-series/${seriesId}/reminders`));
      if (res.ok) {
        const data = await res.json();
        setDefaults(data.defaults || null);
        setTemplates(data.templates || { email: [], sms: [] });
        if (data.configured && data.config) {
          setCfg({
            enabled: data.config.enabled,
            offsets_minutes: data.config.offsets_minutes || [1440, 120],
            channels: data.config.channels || 'both',
            timezone: data.config.timezone || 'America/Los_Angeles',
            email_subject: data.config.email_subject,
            email_body: data.config.email_body,
            sms_body: data.config.sms_body,
            email_template_id: data.config.email_template_id ?? null,
            sms_template_id: data.config.sms_template_id ?? null,
          });
        }
      }
    } catch (err) {
      console.error('Error loading reminder config:', err);
    } finally {
      setLoading(false);
    }
  }, [seriesId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadSends = async () => {
    setShowSends((v) => !v);
    try {
      const res = await authFetch(getWeaveinApiUrl(`/api/workforce/training-series/${seriesId}/reminders/sends`));
      if (res.ok) {
        const data = await res.json();
        setSends(data.sends || []);
      }
    } catch (err) {
      console.error('Error loading reminder sends:', err);
    }
  };

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const res = await authFetch(getWeaveinApiUrl(`/api/workforce/training-series/${seriesId}/reminders`), {
        method: 'PUT',
        jsonBody: cfg,
      });
      if (res.ok) {
        setMsg('Reminders saved. Due reminders send automatically (checked every 5 minutes).');
      } else {
        const data = await res.json().catch(() => ({} as any));
        setMsg(typeof data.detail === 'string' ? data.detail : 'Could not save reminders');
      }
    } catch (err) {
      console.error('Error saving reminders:', err);
      setMsg('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleOffset = (minutes: number) =>
    setCfg((c) => ({
      ...c,
      offsets_minutes: c.offsets_minutes.includes(minutes)
        ? c.offsets_minutes.filter((m) => m !== minutes)
        : [...c.offsets_minutes, minutes],
    }));

  if (loading) {
    return (
      <div className="py-6 text-center text-ink-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading reminders…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-ink flex items-center gap-2">
          <Bell className="w-4 h-4 text-emerald-600" /> Class reminders
        </h3>
        <label className="flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            checked={cfg.enabled}
            onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
            className="rounded border-v2border text-emerald-600 focus:ring-emerald-500"
          />
          Enabled
        </label>
      </div>

      <div>
        <p className="text-xs font-medium text-ink-500 mb-1.5">Send via</p>
        <div className="flex items-center gap-2">
          {(['email', 'sms', 'both'] as const).map((ch) => (
            <button
              key={ch}
              onClick={() => setCfg({ ...cfg, channels: ch })}
              className={`px-3 py-1.5 rounded-lg border text-sm capitalize ${
                cfg.channels === ch
                  ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                  : 'border-v2border text-ink-500 hover:bg-paper-2'
              }`}
            >
              {ch === 'sms' ? 'Text (SMS)' : ch === 'both' ? 'Email + Text' : 'Email'}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-medium text-ink-500 mb-1.5">When (before each session)</p>
        <div className="flex flex-wrap gap-2">
          {OFFSET_CHOICES.map((o) => (
            <button
              key={o.minutes}
              onClick={() => toggleOffset(o.minutes)}
              className={`px-2.5 py-1 rounded-full border text-xs ${
                cfg.offsets_minutes.includes(o.minutes)
                  ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                  : 'border-v2border text-ink-500 hover:bg-paper-2'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {(cfg.channels === 'email' || cfg.channels === 'both') && (
        <div className="space-y-2">
          {templates.email.length > 0 && (
            <div>
              <label className="text-[11px] text-ink-500">Email template (Communications)</label>
              <select
                value={cfg.email_template_id ?? ''}
                onChange={(e) => setCfg({ ...cfg, email_template_id: e.target.value ? Number(e.target.value) : null })}
                className="w-full px-3 py-2 border border-v2border rounded-lg text-sm bg-surface"
              >
                <option value="">Custom text (below)</option>
                {templates.email.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!cfg.email_template_id && (
            <>
              <input
                type="text"
                placeholder={defaults?.email_subject || 'Email subject'}
                value={cfg.email_subject || ''}
                onChange={(e) => setCfg({ ...cfg, email_subject: e.target.value || null })}
                className="w-full px-3 py-2 border border-v2border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <textarea
                rows={4}
                placeholder={defaults?.email_body || 'Email body'}
                value={cfg.email_body || ''}
                onChange={(e) => setCfg({ ...cfg, email_body: e.target.value || null })}
                className="w-full px-3 py-2 border border-v2border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </>
          )}
        </div>
      )}
      {(cfg.channels === 'sms' || cfg.channels === 'both') && (
        <div className="space-y-2">
          {templates.sms.length > 0 && (
            <div>
              <label className="text-[11px] text-ink-500">Text template (Communications)</label>
              <select
                value={cfg.sms_template_id ?? ''}
                onChange={(e) => setCfg({ ...cfg, sms_template_id: e.target.value ? Number(e.target.value) : null })}
                className="w-full px-3 py-2 border border-v2border rounded-lg text-sm bg-surface"
              >
                <option value="">Custom text (below)</option>
                {templates.sms.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!cfg.sms_template_id && (
            <textarea
              rows={2}
              placeholder={defaults?.sms_body || 'Text message'}
              value={cfg.sms_body || ''}
              onChange={(e) => setCfg({ ...cfg, sms_body: e.target.value || null })}
              className="w-full px-3 py-2 border border-v2border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          )}
        </div>
      )}
      <p className="text-xs text-ink-400">
        Placeholders: {'{{first_name}} {{session_title}} {{session_time}} {{location}} {{meeting_link}} {{org_name}}'}.
        Blank fields use the default message. Members who opted out are skipped automatically.
      </p>

      {msg && <p className="text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">{msg}</p>}

      <div className="flex items-center justify-between">
        <button onClick={loadSends} className="text-sm text-ink-500 hover:text-ink underline">
          {showSends ? 'Hide send log' : 'View send log'}
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save reminders
        </button>
      </div>

      {showSends && (
        <div className="border border-v2border rounded-lg overflow-hidden">
          {sends.length === 0 ? (
            <p className="text-sm text-ink-500 p-3">No reminders sent yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-paper text-xs text-ink-500">
                <tr>
                  <th className="text-left px-3 py-2">Recipient</th>
                  <th className="text-left px-3 py-2">Session</th>
                  <th className="text-left px-3 py-2">Channel</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Sent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-v2border-muted">
                {sends.slice(0, 25).map((s) => (
                  <tr key={s.id}>
                    <td className="px-3 py-2">{s.recipient || '—'}</td>
                    <td className="px-3 py-2">{s.session_title}</td>
                    <td className="px-3 py-2 uppercase text-xs">{s.channel}</td>
                    <td className={`px-3 py-2 text-xs ${s.status === 'sent' ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {s.status.replace(/_/g, ' ')}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-500">{new Date(s.sent_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
